/**
 * A component that renders a PDF page with customizable layout and content.
 *
 * @component
 * @param {React.ReactNode} children - The content to be rendered inside the page
 * @param {string} [paddings='48px 36px 43px 36px'] - CSS padding values for the page
 * @param {boolean} [shouldShowHeaderAndFooter=false] - Whether to show the header and footer
 * @param {string} [sectionNumber=''] - The section number to display in the header
 * @param {string} [sectionTitle=''] - The section title to display in the header
 * @param {boolean} [withEmptySpaceImage] - Whether to show an empty space image
 * @returns {JSX.Element} A PDF page component with the specified layout and content
 * @see {@link https://v3.react-pdf.org/components#page | React-PDF}
 */

import React from 'react';

import { Page, StyleSheet } from '@react-pdf/renderer';

import { EmptySpaceImage } from './EmptySpaceImage';
import { PageNumberFooter } from './PDFHeaderAndFooter/PageNumberFooter';
import { SectionTitleHeader } from './PDFHeaderAndFooter/SectionTitleHeader';

interface IPDFPageProps {
  children: React.ReactNode;
  paddings?: string;
  shouldShowHeaderAndFooter?: boolean;
  sectionNumber?: string;
  sectionTitle?: string;
  withEmptySpaceImage?: boolean;
  isStandardOrLightTariff: boolean
}

export const PDFPage = ({
  children,
  paddings = '48px 36px 43px 36px',
  shouldShowHeaderAndFooter = false,
  sectionNumber = '',
  sectionTitle = '',
  withEmptySpaceImage,
  isStandardOrLightTariff
}: IPDFPageProps) => (
  <Page wrap size={'A4'} style={[{ padding: paddings }, styles.page]}>
    {children}
    {shouldShowHeaderAndFooter && withEmptySpaceImage && <EmptySpaceImage />}
    {shouldShowHeaderAndFooter && <SectionTitleHeader number={sectionNumber} title={sectionTitle} />}
    {shouldShowHeaderAndFooter && <PageNumberFooter isStandardOrLightTariff={isStandardOrLightTariff}/>}
  </Page>
);

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    height: 842,
    width: 595,
    color: '#212121',
  },
});
